import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Chip } from '../components/ui/Chip'
import { EmptyState } from '../components/ui/EmptyState'
import { apiClient } from '../lib/api/client'
import { AddProjectAffordance } from '../verticals/orders/AddProjectAffordance'
import { OrderRowMenu } from '../verticals/orders/OrderRowMenu'
import { PartThumbnails } from '../verticals/orders/PartThumbnails'
import {
  ORDER_STATUS_CHIP_META,
  formatEstDelivery,
  formatMoney,
  formatOrderIdentitySubtitle,
  formatPaymentType,
  resolveOrderIdentity,
} from '../verticals/orders/domain'
import type { OrderSummary } from '../verticals/orders/domain'

/** Most-recent rows shown in the Home "Recent activity" Orders tab (UID-90). */
const MAX_ROWS = 6

/** Accessible name for the recent-orders card list (was the `<table>`'s caption). */
const RECENT_ORDERS_LABEL = 'Your 6 most recent orders'

type LoadState = 'idle' | 'loading' | 'loaded' | 'error'

export interface RecentOrdersProps {
  /** Only fetch when the session is authenticated — `order:read` is an
   * account-baseline scope, but there's no session to scope it to when
   * signed out, so we skip the call entirely rather than let it 401. */
  readonly authenticated: boolean
  /** Reports the live count back up to the parent's `Tabs` `count` badge —
   * driven from the fetched list length, never hardcoded (the UID-27 gap
   * this ticket closes). */
  readonly onCountChange: (count: number) => void
}

/**
 * Live "Recent orders" widget for the Home activity panel (UID-90). Reuses
 * the orders vertical's existing `GET /orders` client call, `OrderSummary`
 * type, and status-chip metadata (UID-63/ADR-0036) rather than re-fetching
 * or re-typing any of it — this is a read-only, capped (most-recent 5) view
 * of the same data the full `/orders` screen shows.
 *
 * S5 (UID-99): renders ORDER NAME + EST. DELIVERY (design prototype's
 * "Recent orders" block, home L452) via the SAME `resolveOrderIdentity` /
 * `formatEstDelivery` view-model helpers `OrderTable` uses — B1 (PR #105)
 * landed `orderName`/`estDeliveryDate` on `OrderSummary` server-side, closing
 * the gap the UID-90 scope note deferred (there is no longer a value to
 * fabricate; the server derives both).
 *
 * UID-132 (design-fidelity pass): brings this widget's row up to the SAME
 * fuller field set the design's home `ordersTable` markup shows (`Fictiv
 * Platform.dc.html` ~L452-488) — PROJECT, per-part thumbnails, PAYMENT TYPE,
 * and a row ⋮ actions menu — matching `OrderTable`'s `/orders` row exactly
 * (same shared components/helpers, so the two surfaces can't drift).
 */
export function RecentOrders({ authenticated, onCountChange }: RecentOrdersProps) {
  const [loadState, setLoadState] = useState<LoadState>('idle')
  const [orders, setOrders] = useState<readonly OrderSummary[]>([])

  useEffect(() => {
    if (!authenticated) {
      setLoadState('idle')
      setOrders([])
      onCountChange(0)
      return
    }

    let cancelled = false
    setLoadState('loading')

    async function load() {
      try {
        // Ask for exactly what this widget renders. It previously fetched up to
        // the server's full 200-order hard cap to show six rows, on the Home
        // page, on every visit.
        //
        // `limit` bounds `items` ONLY — it deliberately does NOT narrow
        // `spend.pipeline`, whose four tiles are the Orders TABLE's filter
        // controls and must keep describing the set they filter (ADR-0126
        // D1/D2). This widget renders rows and no tiles, so it is the consumer
        // that parameter exists for; the Orders page passes no `limit`.
        const { data, error } = await apiClient.GET('/orders', {
          params: { query: { limit: MAX_ROWS } },
        })
        if (cancelled) return
        if (data) {
          setOrders(data.items)
          setLoadState('loaded')
          // Now at most `MAX_ROWS` rather than the account's order count, since
          // the fetch is bounded. UNOBSERVABLE: the only call site
          // (`HomePage`'s `handleOrdersCountChange`) is a documented no-op —
          // Home's tab badge was removed per the design (ADR-0095 D5), which is
          // also why `OpenQuotes` never grew this prop. If a real consumer of
          // this callback ever appears it must read a server-supplied total, not
          // a page length; there is no such total on `OrdersListResponse` today
          // (`spend.orderCount` is date-windowed and is not it).
          onCountChange(data.items.length)
          return
        }
        if (error && (error as { status?: number }).status === 401) {
          return // Handled globally by authMiddleware's session invalidation.
        }
        setLoadState('error')
        onCountChange(0)
      } catch {
        if (cancelled) return
        setLoadState('error')
        onCountChange(0)
      }
    }

    void load()

    return () => {
      cancelled = true
    }
    // `onCountChange` is expected to be referentially stable from the caller
    // (a `useState` setter or equivalent) — including it here is correct but
    // would not itself change fetch behavior.
  }, [authenticated, onCountChange])

  if (!authenticated || loadState === 'idle') {
    return (
      <EmptyState title="No orders yet" detail="Orders and RFQs you submit will show up here." />
    )
  }

  if (loadState === 'loading') {
    return <RecentOrdersSkeleton />
  }

  if (loadState === 'error') {
    return (
      <EmptyState
        title="Couldn't load your recent orders"
        detail="Something went wrong loading your orders. Try refreshing the page."
      />
    )
  }

  if (orders.length === 0) {
    return (
      <EmptyState title="No orders yet" detail="Orders and RFQs you submit will show up here." />
    )
  }

  // The server already bounded this to `MAX_ROWS` (see the fetch above). The
  // slice is KEPT as a defensive cap rather than deleted, and unlike
  // `OpenQuotes` that is the right call here: it encodes no policy the server
  // also encodes — there is no status filter to disagree about, just "never
  // render more than six rows" — so the two cannot drift apart, and a server
  // that ever ignored `limit` would still not break the card's layout.
  const rows = orders.slice(0, MAX_ROWS)

  // UID-212 follow-up: the design's Home "Recent orders" is a bordered CARD with grid rows —
  // NOT a column-header table. Each row carries its own inline mini-labels (Project / Est. delivery
  // / Total / Payment type) above the values (`Fictiv Platform.dc.html` home ordersTable). Rendered
  // as an accessible list (ul/li) rather than a `<table>` since there is no header row.
  return (
    <ul className="recent-orders-card" aria-label={RECENT_ORDERS_LABEL}>
      {rows.map((order) => (
        <RecentOrderRow key={order.id} order={order} />
      ))}
    </ul>
  )
}

function RecentOrderRow({ order }: { readonly order: OrderSummary }) {
  const meta = ORDER_STATUS_CHIP_META[order.status]
  const identity = resolveOrderIdentity(order)
  const orderLabel = identity.name ?? identity.ref

  return (
    <li className="recent-order-row">
      <div className="recent-order-cell recent-order-identity">
        {/* Order NAME primary, "SO-##### · Qty N" subtitle (no inline label — the name IS the cell). */}
        <Link to={`/orders/${order.id}`} className="order-row-identity-primary">
          {orderLabel}
        </Link>
        <span className="order-row-identity-subtitle">{formatOrderIdentitySubtitle(order)}</span>
      </div>
      <div className="recent-order-cell">
        <span className="recent-order-cell-label">Project</span>
        <AddProjectAffordance orderLabel={orderLabel} />
      </div>
      <div className="recent-order-cell recent-order-parts">
        <PartThumbnails count={order.lineCount} thumbnailUrls={order.thumbnailUrls} />
      </div>
      <div className="recent-order-cell recent-order-status">
        {/* squarish, no-icon, colored status pill (UID-212 follow-up). */}
        <Chip tone={meta.tone}>{meta.label}</Chip>
      </div>
      <div className="recent-order-cell">
        <span className="recent-order-cell-label">Est. delivery</span>
        <span className="recent-order-cell-value">{formatEstDelivery(order)}</span>
      </div>
      <div className="recent-order-cell">
        <span className="recent-order-cell-label">Total</span>
        <span className="recent-order-cell-value">
          {formatMoney(Number(order.total.amount), order.total.currency)}
        </span>
      </div>
      <div className="recent-order-cell">
        <span className="recent-order-cell-label">Payment type</span>
        <span className="recent-order-cell-value">{formatPaymentType(order)}</span>
      </div>
      <div className="recent-order-cell recent-order-menu">
        <OrderRowMenu orderId={order.id} orderLabel={orderLabel} />
      </div>
    </li>
  )
}

function RecentOrdersSkeleton() {
  return (
    <ul className="recent-orders-card" aria-hidden="true">
      {Array.from({ length: MAX_ROWS }, (_, index) => (
        <li
          // biome-ignore lint/suspicious/noArrayIndexKey: fixed-length, never-reordered skeleton rows
          key={index}
          className="recent-order-row recent-order-row-skeleton"
        >
          <div className="skeleton" style={{ width: '55%', height: 16 }} />
        </li>
      ))}
    </ul>
  )
}
